import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { Box, Typography, Link as MuiLink, IconButton, Tooltip, FormControl, InputLabel, Select, MenuItem, FormControlLabel, Switch, TextField, Button, CircularProgress, Paper, Dialog, DialogContent, DialogTitle, useTheme, Fade, useMediaQuery, Table, TableBody, TableCell, TableContainer, TableRow, Grid, TableHead, Card, CardContent, Divider } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, BarChart, Bar, ComposedChart, TooltipProps } from 'recharts';
import AssessmentIcon from '@mui/icons-material/Assessment';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import CloseIcon from '@mui/icons-material/Close';
import { Data, MeasurementData, getStyledValue } from './utils';
import { Link } from 'react-router-dom';
import { SelectChangeEvent } from '@mui/material/Select';
import { green } from '@mui/material/colors';
import { format, parseISO, subDays, addMinutes } from 'date-fns';
import BarChartIcon from '@mui/icons-material/BarChart';
import SimCardIcon from '@mui/icons-material/SimCard';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import MemoryIcon from '@mui/icons-material/Memory';
import RouterIcon from '@mui/icons-material/Router';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import ServiceChartsDialog from './ServiceChartsDialog';

interface DetailsPanelProps {
  isMobile: boolean;
  selectedDevice?: Data;
  selectedBts?: any[];  // Updated type to array of BTS
  measurementData?: MeasurementData[];
  loadingMeasurements?: boolean;
  timeRange?: string;
  customTimeRange?: string;
  customTimeUnit?: string;
  handleTimeRangeChange?: (event: SelectChangeEvent<string>) => void;
  handleCustomTimeRangeChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleCustomTimeUnitChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleCustomTimeSubmit?: () => void;
  isLoadingNewData?: boolean;
  selectedService?: ServiceData;
  onMarkerClick?: (boxNumber: string) => void;
}

interface ServiceData extends Data {
  total_vpna: number;
  total_vpns: number;
  total_vpnb: number;
  total_ping: number;
  id_esa: string;
  id_instalacji: string;
  box: number;  // Zakładamy, że box jest numerem
}

const networkColor = (network: string) => {
  switch (network) {
    case 'Orange':
      return 'orange';
    case 'Plus':
      return '#66bb6a'; // jaśniejszy zielony dla Plusa
    case 'Play':
      return '#ba68c8'; // jaśniejszy fioletowy dla Play
    default:
      return 'black';
  }
};

const getPercentageColor = (percentage: number): string => {
  if (percentage > 83.3) return 'green';
  if (percentage > 47.6) return '#FFD700'; // Darker yellow (gold)
  return 'red';
};

const formatPercentage = (value: number): string => {
  const percentage = (value / 42) * 100;
  return `${percentage.toFixed(1)}%`;
};







// Helper function to convert uptime string to minutes
const uptimeToMinutes = (uptimeString: string): number => {
  if (uptimeString.includes(':')) {
    const [hours, minutes] = uptimeString.split(':').map(Number);
    return hours * 60 + minutes;
  }
  return Number(uptimeString);
};



const DetailsPanel: React.FC<DetailsPanelProps> = ({
  isMobile,
  selectedDevice,
  selectedBts,
  measurementData = [],
  loadingMeasurements = false,
  timeRange,
  customTimeRange,
  customTimeUnit,
  handleTimeRangeChange,
  handleCustomTimeRangeChange,
  handleCustomTimeUnitChange,
  handleCustomTimeSubmit,
  isLoadingNewData = false,
  selectedService,
  onMarkerClick,
}) => {
  
  const [openChartPopup, setOpenChartPopup] = useState(false);
  const theme = useTheme();
  const isDarkMode = theme.name === 'dark';
  const [chartHeight, setChartHeight] = useState(300);
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [openServiceChartPopup, setOpenServiceChartPopup] = useState(false);
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const metricColors = {
    
    PM10: '#8884d8',
    PM25: '#82ca9d',
    AMBIENT_TEMP: '#ff7300',
    AMBIENT_HUMID: '#0088FE',
    AMBIENT_PRESS: '#A52A2A',
    JAS: '#82ca9d',
    DUST_HEATER_NTC: '#FF6347',
    NH3_raw: '#9C27B0', // Purple
    H2S_raw: '#FF9800', // Orange
    ODO: '#00BCD4',    // Cyan
  };

  // Add this function to format uptime
  const formatUptime = (uptimeString: string): string => {
    if (!uptimeString) return 'N/A';
    if (uptimeString.includes(':')) return uptimeString;
    return `0:${uptimeString.padStart(2, '0')}`;
  };

  // Add this function to get the last non-null value for a given field
  const getLastNonNullValue = (data: any[], field: string) => {
    for (let i = data.length - 1; i >= 0; i--) {
      if (data[i][field] !== null && data[i][field] !== undefined && data[i][field] !== 'N/A') {
        return data[i][field];
      }
    }
    return 'N/A';
  };

  useEffect(() => {
    const updateChartHeight = () => {
      if (chartContainerRef.current) {
        const containerHeight = chartContainerRef.current.clientHeight; 
        setChartHeight(containerHeight * 0.8);
      }
    };

    updateChartHeight();
    window.addEventListener('resize', updateChartHeight);

    return () => {
      window.removeEventListener('resize', updateChartHeight);
    };
  }, []);

  const formatDate = (dateString: string) => {
    if (!dateString || dateString === 'dataMin' || dateString === 'dataMax') {
      return "N/A"; // Return a default value for invalid dates
    }
    try {
      const date = parseISO(dateString);
      return format(date, "dd/MM/yyyy HH:mm:ss");
    } catch (error) {
      
      return "Invalid Date";
    }
  };

  const formatTime = (dateString: string) => {
    if (!dateString || dateString === 'dataMin' || dateString === 'dataMax') {
      return "N/A"; // Return a default value for invalid times
    }
    try {
      const date = parseISO(dateString);
      return format(date, "HH:mm");
    } catch (error) {
      
      return "Invalid Time";
    }
  };

  const renderPreviewChart = () => (
    <Box sx={{ width: '100%', height: isMobile ? '30vh' : '40vh', display: 'flex', justifyContent: 'flex-start', mt: 4 }}>
      <ResponsiveContainer width="95%" height="100%">
        <LineChart data={measurementData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="time_stamp"
            tickFormatter={(time) => formatTime(time)}
            stroke={isDarkMode ? '#fff' : '#666'}
          />
          <YAxis stroke={isDarkMode ? '#fff' : '#666'} />
          <RechartsTooltip
            labelFormatter={(label) => formatDate(label)}
            formatter={(value) => [parseFloat(value as string).toFixed(2), "PM10"]}
            contentStyle={{
              backgroundColor: isDarkMode ? '#333' : '#fff',
              border: `1px solid ${isDarkMode ? '#666' : '#ccc'}`,
              color: isDarkMode ? '#fff' : '#333',
            }}
          />
          <Legend
            wrapperStyle={{
              color: isDarkMode ? '#fff' : '#333',
            }}
          />
          <Line type="monotone" dataKey="pm10" stroke="#8884d8" dot={false} strokeWidth={2} />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );

  const renderFullChart = (dataKey: string, color: string, title: string) => (
    <Box sx={{ width: '100%', height: '40vh', mb: 4, position: 'relative' }}>
      <Typography variant="h6" gutterBottom>{title}</Typography>
      <Fade in={isLoadingNewData} unmountOnExit>
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          zIndex: 1,
        }}>
          <CircularProgress />
        </Box>
      </Fade>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={measurementData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="time_stamp"
            tickFormatter={(time) => formatTime(time)}
            stroke={isDarkMode ? '#fff' : '#666'}
          />
          <YAxis stroke={isDarkMode ? '#fff' : '#666'} />
          <RechartsTooltip
            labelFormatter={(label) => formatDate(label)}
            formatter={(value) => [parseFloat(value as string).toFixed(2), title]}
            contentStyle={{
              backgroundColor: isDarkMode ? '#333' : '#fff',
              border: `1px solid ${isDarkMode ? '#666' : '#ccc'}`,
              color: isDarkMode ? '#fff' : '#333',
            }}
          />
          <Legend
            wrapperStyle={{
              color: isDarkMode ? '#fff' : '#333',
            }}
          />
          <Line type="monotone" dataKey={dataKey} stroke={color} dot={false} strokeWidth={3} />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );

  const renderTimeRangeControls = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <FormControl fullWidth variant="outlined" size="small" sx={{ mt: 2, display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
        <InputLabel id="time-range-select-label">Time Range</InputLabel>
        <Select
          labelId="time-range-select-label"
          value={timeRange}
          onChange={handleTimeRangeChange}
          label="Time Range"
        >
          <MenuItem value="custom">Custom</MenuItem>
          <MenuItem value="1h">1 Hour</MenuItem>
          <MenuItem value="12h">12 Hours</MenuItem>
          <MenuItem value="1d">1 Day</MenuItem>
          <MenuItem value="7d">7 Days</MenuItem>
          <MenuItem value="30d">30 Days</MenuItem>
        </Select>
        {timeRange === 'custom' && (
          <>
            <FormControlLabel
              control={
                <Switch
                  checked={customTimeUnit === 'days'}
                  onChange={handleCustomTimeUnitChange}
                  name="customTimeUnitSwitch"
                  color="primary"
                />
              }
              label={customTimeUnit === 'days' ? 'Days' : 'Hours'}
              sx={{ ml: 2 }}
            />
            <TextField
              variant="outlined"
              size="small"
              label={`Custom Time Range (${customTimeUnit})`}
              placeholder={`e.g. 5 ${customTimeUnit}`}
              value={customTimeRange}
              onChange={handleCustomTimeRangeChange}
              sx={{ ml: 2 }}
            />
            <Tooltip title={isLoadingNewData ? "Loading..." : "Apply custom time range"}>
              <span>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleCustomTimeSubmit}
                  disabled={isLoadingNewData}
                  sx={{ ml: 2 }}
                >
                  Apply
                </Button>
              </span>
            </Tooltip>
          </>
        )}
      </FormControl>
      {isLoadingNewData && <CircularProgress size={24} />}
    </Box>
  );

  // Function to get the correct background color
  const getBackgroundColor = (component: 'dialog' | 'chart' | 'paper' | 'card' = 'paper') => {
    if (theme.name === 'dark') {
      switch (component) {
        case 'dialog':
          return '#1a1a1a';  // Darkest - base layer
        case 'chart':
          return '#2d2d2d';  // Lighter - for charts
        case 'paper':
          return '#262626';  // Medium - for paper components
        case 'card':
          return '#333333';  // Lightest - for cards
        default:
          return '#262626';
      }
    }
    // Light theme colors
    switch (component) {
      case 'dialog':
        return '#ffffff';
      case 'chart':
        return '#f8f8f8';
      case 'paper':
        return '#ffffff';
      case 'card':
        return '#f5f5f5';
      default:
        return '#ffffff';
    }
  };



  const CustomTooltip = ({ active, payload, label }: TooltipProps<any, any>) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          backgroundColor: getBackgroundColor(),
          border: `1px solid ${theme.palette.divider}`,
          color: theme.palette.text.primary,
          padding: '10px',
        }}>
          <p>{formatDate(label)}</p>
          {payload.map((entry, index) => (
            <p key={index}>
              {entry.name === 'Load (5m avg)'
                ? `${entry.name}: ${parseFloat(entry.value).toFixed(2)}`
                : entry.name === 'Uptime'
                  ? `${entry.name}: ${formatUptime(entry.payload.rawUptime)}`
                  : `${entry.name}: ${entry.value}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const openServiceChartDialog = () => {
    setOpenServiceChartPopup(true);
  };

  const renderServiceDetails = (service: ServiceData) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>Box {service.box}</Typography>
        <MuiLink component={Link} to={`/device/${selectedDevice.box}`} style={{ marginLeft: 'auto' }}>
          <Tooltip title="Show device details">
            <IconButton color="primary" aria-label="Go to device panel">
              <AssessmentIcon />
            </IconButton>
          </Tooltip>
        </MuiLink>
      </Box>
      
      <Paper elevation={3} sx={{ 
        p: 2, 
        bgcolor: getBackgroundColor('paper'),
        borderRadius: 2,
        boxShadow: theme.name === 'dark' ? '0 4px 6px rgba(0,0,0,0.3)' : 1,
      }}>
        <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>Device Information</Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body1"><strong>Modem:</strong> {service.modem}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body1"><strong>GPS:</strong> {service.gps}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body1"><strong>Time Stamp:</strong> {formatDate(service.time_stamp)}</Typography>
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={3} sx={{ 
        p: 2, 
        bgcolor: getBackgroundColor('paper'),
        borderRadius: 2,
        boxShadow: theme.name === 'dark' ? '0 4px 6px rgba(0,0,0,0.3)' : 1,
      }}>
        <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>Connectivity Data (Last Week)</Typography>
        <Grid container spacing={2}>
          {[
            { label: 'Total VPN A', value: service.total_vpna },
            { label: 'Total VPN S', value: service.total_vpns },
            { label: 'Total VPN B', value: service.total_vpnb },
            { label: 'Total Ping', value: service.total_ping }
          ].map((item, index) => (
            <Grid item xs={3} key={index}>
              <Tooltip title={`Actual value: ${item.value}`}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2">{item.label}</Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      color: getPercentageColor(item.value / 42 * 100),
                      fontWeight: 'bold'
                    }}
                  >
                    {formatPercentage(item.value)}
                  </Typography>
                </Box>
              </Tooltip>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Paper elevation={3} sx={{ 
        p: 2, 
        bgcolor: getBackgroundColor('paper'),
        borderRadius: 2,
        boxShadow: theme.name === 'dark' ? '0 4px 6px rgba(0,0,0,0.3)' : 1,
      }}>
        <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>Additional Information</Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body1"><strong>ID ESA:</strong> {service.id_esa}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body1"><strong>ID Instalacji:</strong> {service.id_instalacji}</Typography>
          </Grid>
        </Grid>
      </Paper>

      <Button
        variant="contained"
        startIcon={<BarChartIcon />}
        onClick={() => setOpenServiceChartPopup(true)}
        sx={{ mt: 2, alignSelf: 'flex-start' }}
      >
        View Service Measurements
      </Button>
    </Box>
  );

  // Uproszczona wersja resetDialogStates
  const resetDialogStates = () => {
    setOpenServiceChartPopup(false);
  };

  return (
    <Paper 
      elevation={2} 
      sx={{ 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: isDarkMode ? 'background.paper' : 'background.paper',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'borders.primary',
      }}
    >
      {selectedDevice ? (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          height: '100%',
          p: 2,
        }}>
          {/* Nagłówek */}
          <Box sx={{ 
            borderBottom: 1,
            borderColor: isDarkMode ? 'rgba(255, 255, 255, .125)' : '#e0e0e0',
            pb: 2,
            mb: 2
          }}>
            <Typography variant="h6" sx={{ 
              color: isDarkMode ? '#64B5F6' : 'primary.main',
              fontWeight: 'bold' 
            }}>
              Device Details
            </Typography>
          </Box>

          {/* Reszta zawartości */}
          <Box sx={{ 
            flex: 1,
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
            },
            '&::-webkit-scrollbar-thumb': {
              background: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
              borderRadius: '4px',
            },
          }}>
            {selectedDevice ? (
              'total_vpna' in selectedDevice ? (
                <>
                  {renderServiceDetails(selectedDevice as ServiceData)}
                  <ServiceChartsDialog
                    open={openServiceChartPopup}
                    onClose={() => {
                      setOpenServiceChartPopup(false);
                      resetDialogStates();
                    }}
                    boxNumber={selectedDevice.box}
                  />
                </>
              ) : (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="h6">Box {selectedDevice.box}</Typography>
                    <MuiLink component={Link} to={`/device/${selectedDevice.box}`} style={{ marginLeft: 'auto' }}>
                      <Tooltip title="Show device details">
                        <IconButton color="primary" aria-label="Go to device panel">
                          <AssessmentIcon />
                        </IconButton>
                      </Tooltip>
                    </MuiLink>
                  </Box>
                  <Typography variant="body1">Modem: {selectedDevice.modem}</Typography>
                  <Typography variant="body1">GPS Date: {formatDate(selectedDevice.gps_time_stamp)}</Typography>
                  <Typography variant="body1">
                    PM10: {getStyledValue(parseFloat(selectedDevice.pm10).toFixed(2), 'pm10')}
                  </Typography>
                  <Typography variant="body1">
                    PM25: {getStyledValue(parseFloat(selectedDevice.pm25).toFixed(2), 'pm25')}
                  </Typography>
                  <Typography variant="body1">Measurements Date: {formatDate(selectedDevice.measurement_time)}</Typography>

                  {renderTimeRangeControls()}

                  {loadingMeasurements ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : measurementData.length > 0 ? (
                    <>
                      {renderPreviewChart()}
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 4 }}>
                        <Button
                          variant="outlined"
                          startIcon={<FullscreenIcon />}
                          onClick={() => setOpenChartPopup(true)}
                        >
                          View All Measurements
                        </Button>
                      </Box>
                    </>
                  ) : (
                    <Typography variant="body1" sx={{ mt: 4 }}>No measurement data available for this device.</Typography>
                  )}

                  <Dialog
                    open={openChartPopup}
                    onClose={() => setOpenChartPopup(false)}
                    maxWidth={false}
                    fullWidth

                    PaperProps={{
                      sx: {
                        width: '95vw',
                        maxWidth: '95vw',
                        height: '90vh',
                        

                      },
                    }}
                  >
                    <DialogTitle sx={{
                      m: 0,
                      p: 2,
                      bgcolor: isDarkMode ? '#0f0f0f' : '#1976d2',
                      color: '#FFFFFF'
                    }}>
                      <IconButton
                        aria-label="close"
                        onClick={() => setOpenChartPopup(false)}
                        sx={{
                          position: 'absolute',
                          left: 8,
                          top: 12,
                          color: '#FFFFFF',
                        }}
                      >
                        <CloseIcon />
                      </IconButton>
                      <Typography variant="h6" component="div" sx={{ pl: 6 }}>
                        Detailed Charts for Box {selectedDevice.box}
                      </Typography>
                    </DialogTitle>
                    <DialogContent sx={{
                      p: 4,
                      
                      color: isDarkMode ? '#E0E0E0' : 'inherit',
                      position: 'relative',
                    }}>
                      {renderTimeRangeControls()}
                      <Box sx={{ mt: 4 }}>
                        {renderFullChart("pm10", "#8884d8", "PM10 ")}
                        {renderFullChart("pm25", "#82ca9d", "PM2.5 ")}
                      </Box>
                      <Fade in={isLoadingNewData} unmountOnExit>
                        <Box sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(255, 255, 255, 0.7)',
                          zIndex: 1000,
                        }}>
                          <CircularProgress size={60} />
                        </Box>
                      </Fade>
                    </DialogContent>
                  </Dialog>
                </Box>
              )
            ) : (
              <Box sx={{ p: 2 }}>
                <Typography variant="body1">Select a device to see details</Typography>
              </Box>
            )}
          </Box>
        </Box>
      ) : (
        <Box sx={{ p: 2 }}>
          {selectedBts && selectedBts.length > 0 ? (
            <Box>
              <Box sx={{ 
                borderBottom: 1,
                borderColor: isDarkMode ? 'rgba(255, 255, 255, .125)' : '#e0e0e0',
                pb: 2,
                mb: 2
              }}>
                <Typography variant="h6" sx={{ 
                  color: isDarkMode ? '#64B5F6' : 'primary.main',
                  fontWeight: 'bold' 
                }}>
                  BTS Details
                </Typography>
              </Box>
              
              <Box sx={{ 
                maxHeight: '70vh',
                overflowY: 'auto',
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                  borderRadius: '4px',
                },
              }}>
                {selectedBts
                  .sort((a, b) => a.distance - b.distance)
                  .map((bts, index) => (
                    <Paper 
                      key={bts.station_id} 
                      elevation={3} 
                      sx={{ 
                        p: 2, 
                        mb: 2,
                        bgcolor: getBackgroundColor('paper'),
                        borderRadius: 2,
                        boxShadow: theme.name === 'dark' ? '0 4px 6px rgba(0,0,0,0.3)' : 1,
                        borderLeft: `4px solid ${networkColor(bts.siec_id)}`,
                      }}
                    >
                      <Typography variant="h6" sx={{ 
                        mb: 1, 
                        color: 'primary.main',
                        display: 'flex',
                        alignItems: 'center'  
                      }}>
                        <RouterIcon sx={{ mr: 1, color: networkColor(bts.siec_id) }} />
                        Station {bts.station_id}
                      </Typography>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>Network</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 'medium', color: networkColor(bts.siec_id) }}>
                            {bts.siec_id}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>Distance</Typography>
                          <Typography variant="body1">
                            {bts.distance.toFixed(2)} km
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>Location</Typography>
                          <Typography variant="body1">
                            {bts.lokalizacja}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>Coordinates</Typography>
                          <Typography variant="body1">
                            {bts.bts_location}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>Standard</Typography>
                          <Typography variant="body1">
                            {bts.standard}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>Bands</Typography>
                          <Typography variant="body1">
                            {Array.from(new Set(bts.pasma)).join(', ')}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}
              </Box>
            </Box>
          ) : (
            <Typography variant="body1">Select a device to see details</Typography>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default DetailsPanel;

